import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Loader } from './';
import { hashCode } from '../../_core/utils';

const isFloat = n => Number(n) === n && n % 1 !== 0;

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

class ClipImg extends React.PureComponent {
  static defaultProps = {
    isShowSpinner: true,
  };

  static propTypes = {
    src: PropTypes.string,
    x1: PropTypes.number,
    y1: PropTypes.number,
    x2: PropTypes.number,
    y2: PropTypes.number,
    style: PropTypes.object,
    className: PropTypes.string,
    fixHeight: PropTypes.bool,
    windowWidth: PropTypes.number,
    // debug: PropTypes.bool,
    isShowSpinner: PropTypes.bool,
    imageStyles: PropTypes.object,
  }

  constructor(props) {
    super(props);
    const imgHash = hashCode(props.src);
    this.id = btoa(`img${imgHash * Math.random()}${Date.now()}`);
    this.containerId = btoa(`container${imgHash * Math.random()}${Date.now()}`);
  }

  state = {
    loading: false,
    width: 0,
    originalWidth: 0,
    originalHeight: 0,
    scale: 1,
  }

  componentDidMount() {
    this.container = document.getElementById(this.containerId);
    this.mounted = true;
    this.setDimensions();
    this.setOriginalDimensions(this.props.src);
  }

  componentDidUpdate(prevProps) {
    const {
      x1, y1, x2, y2, src, windowWidth,
    } = this.props;
    if (src !== prevProps.src || x1 !== prevProps.x1 || y1 !== prevProps.y1
      || x2 !== prevProps.x2 || y2 !== prevProps.y2 || windowWidth !== prevProps.windowWidth) {
      // console.log('componentDidUpdate', this.props, prevProps);
      // this.setDimensions(true);
      // this.setOriginalDimensions(prevProps.src);
      setTimeout(() => {
        if (this.mounted) {
          this.setDimensions(true);
          this.setOriginalDimensions(this.props.src);
        }
      }, 100);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  onClick = () => {
    this.setDimensions(true);
    this.setOriginalDimensions(this.props.src);
    this.forceUpdate();
  }

  setDimensions = update => {
    if (!this.container) {
      this.container = document.getElementById(this.containerId);
    }
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    const {
      fixHeight, x1, x2, y1, y2,
    } = this.props;

    const loading = update ? isFloat(x1) || isFloat(x2) || isFloat(y1) || isFloat(y2) : this.state.loading;

    // console.log('setDimensions', loading, isFloat(x1), isFloat(x2), isFloat(y1), isFloat(y2));

    const scale = fixHeight ? ((height && height / (y2 - y1)) || 1) : ((width && (width / (x2 - x1))) || 1);

    this.setState({
      width,
      height,
      scale: (Number.isFinite(scale) && scale) || 1,
      loading,
    });
  }

  setOriginalDimensions = (src = this.props.src) => {
    const img = new Image();
    img.onload = () => {
      if (this.mounted) {
        this.setState({ originalWidth: img.width, originalHeight: img.height });
      }
    };
    img.src = src;
  }

  normalizeCropDimensions = () => {
    const {
      x1: oX1, y1: oY1, x2: oX2, y2: oY2,
    } = this.props;
    let x1 = oX1;
    let y1 = oY1;
    let x2 = oX2;
    let y2 = oY2;
    const cropped = !(Number.isNaN(parseInt(x1, 10)) || Number.isNaN(parseInt(y1, 10))
      || Number.isNaN(parseInt(x2, 10)) || Number.isNaN(parseInt(y2, 10)));
    if (!cropped) {
      return {
        x1, y1, x2, y2,
      };
    }
    if (x1 < 0) {
      x2 += (x1 * -1);
      x1 = 0;
    }
    if (y1 < 0) {
      y2 += (y1 * -1);
      y1 = 0;
    }
    return {
      x1, y1, x2, y2,
    };
  }

  render() {
    const {
      className = '', style, src, fixHeight, isShowSpinner, imageStyles = {},
    } = this.props;
    const {
      x1, y1, x2, y2,
    } = this.normalizeCropDimensions();
    const { scale, loading } = this.state;
    const cropped = !(Number.isNaN(parseInt(x1, 10)) || Number.isNaN(parseInt(y1, 10))
      || Number.isNaN(parseInt(x2, 10)) || Number.isNaN(parseInt(y2, 10)));
    let imgStyle = {
      width: '100%',
    };
    const containerStyle = {
      maxWidth: '100%',
      maxHeight: '100%',
      width: '100%',
      height: '100%',
      padding: 0,
      overflow: 'hidden',
    };
    if (cropped) {
      const scaledWidth = this.state.originalWidth * scale;
      const scaledHeight = this.state.originalHeight * scale;
      const croppedWidth = (x2 * scale) - (x1 * scale);
      const opacity = (loading ? 0.1 : 1);
      imgStyle = {
        width: scaledWidth || '100%',
        height: scaledHeight || '100%',
        marginTop: `-${y1 * scale}px`,
        marginLeft: `-${x1 * scale}px`,
        opacity: isShowSpinner ? opacity : 1,
        ...imageStyles,
      };
      if (!fixHeight) {
        const newWidth = (x2 - x1) * scale;
        const newHeight = (y2 - y1) * scale;
        if (newWidth >= this.state.width) {
          containerStyle.width = newWidth;
        }
        if (newHeight >= this.state.height) {
          containerStyle.height = newHeight;
        }
      } else if ((scaledHeight - (y1 * scale)) < this.state.height) {
        const correctTopMargin = this.state.height - (scaledHeight - (y1 * scale));
        imgStyle.marginTop = `-${(y1 * scale) - correctTopMargin}px`;
        // imgStyle.marginTop =
      }
      if (croppedWidth > this.state.width) {
        const correctLeftMargin = ((croppedWidth - this.state.width) / 2) + (x1 * scale);
        imgStyle.marginLeft = `-${correctLeftMargin}px`;
      } else if (croppedWidth < this.state.width) {
        const correctLeftMargin = ((this.state.width - croppedWidth) / 2) - (x1 * scale);
        imgStyle.marginLeft = `${correctLeftMargin}px`;
      }

      if (containerStyle.width > imgStyle.width || containerStyle.height > imgStyle.height) {
        containerStyle.display = 'flex';
        containerStyle.alignItems = 'center';
        containerStyle.justifyContent = 'center';
      }
    } else {
      containerStyle.display = 'flex';
      containerStyle.alignItems = 'center';
      containerStyle.justifyContent = 'center';
      imgStyle.height = '100%';
      imgStyle.objectFit = 'cover';
    }
    return (
      <StyledDiv style={style} className={className} onClick={this.onClick}>
        <div id={this.containerId} style={containerStyle}>
          <img src={src} alt="" style={imgStyle} />
        </div>
        {isShowSpinner && (loading && <Loader className="absolute" />)}
      </StyledDiv>
    );
  }
}

export default ClipImg;
